import {
  decorate,
  observable,
  reaction,
  action,
  computed,
  configure,
  runInAction,
} from 'mobx';
import {
  getFormattedTime,
  getTrimmedString,
} from 'components/project/ClerkScreen/shared/utils/common';
import toast from '../shared/utils/toast';

configure({ enforceActions: 'always' });

class MessageStore {
  constructor(AuctionStore) {
    runInAction('MessageStore', () => {
      this.auctionStore = AuctionStore;
      this.onlineListeners = [];
      this.onlineChatBidders = [];
      this.auctionLog = [];
      this.auctionLogObject = [];
      this.directMessageLog = [];
      this.systemMessage = null;
      this.sellerAction = null;
      this.bidActivityMappings = [
        {
          messageType: -2,
          messageTypeName: 'local',
          bgColor: 'white',
          color: 'black',
          icon: 'circle',
        },
        {
          messageType: -1,
          messageTypeName: 'userError',
          bgColor: 'white',
          color: 'red',
          icon: 'exclamation-triangle',
        },
        {
          messageType: 0,
          messageTypeName: 'message',
          bgColor: 'white',
          color: 'black',
          icon: 'info-circle',
        },
        {
          messageType: 1,
          messageTypeName: 'onlineBid',
          bgColor: 'white',
          color: 'blue',
          icon: 'user-circle-o',
        },
        {
          messageType: 2,
          messageTypeName: 'floorBid',
          bgColor: 'white',
          color: 'green',
          icon: 'user-circle-o',
        },
        {
          messageType: 3,
          messageTypeName: 'laneStatusChange',
          bgColor: 'white',
          color: 'black',
          icon: 'coffee',
        },
        {
          messageType: 4,
          messageTypeName: 'onBlockChanged',
          bgColor: 'black',
          color: 'white',
          icon: 'handshake-o',
        },
        {
          messageType: 5,
          messageTypeName: 'noSale',
          bgColor: 'red',
          color: 'white',
          icon: 'gavel',
        },
        {
          messageType: 6,
          messageTypeName: 'sold',
          bgColor: 'green',
          color: 'white',
          icon: 'gavel',
        },
        {
          messageType: 7,
          messageTypeName: 'subjectTo',
          bgColor: 'yellow',
          color: 'black',
          icon: 'gavel',
        },
        {
          messageType: 8,
          messageTypeName: 'info',
          bgColor: 'white',
          color: 'black',
          icon: 'info-circle',
        },
      ];

      this.messaging = observable({
        disableGlobalTab: true,
        disablePinnedTab: true,
        disableUserTab: true,
        filteredMessageData: null,
        itemNumber: null,
        pinnedMessage: [],
        sendToUserId: null,
        sendToUserName: null,
        sendToMemberName: null,
        userType: 'Bidder',
        isSeller: false,
      });
    });

    reaction(
      () => this.auctionStore.connected,
      () => this.initializeMessaging(),
    );

    reaction(
      () => this.auctionStore.auctionLaneId,
      () => this.initializeMessaging(),
    );
  }


  setSystemMessage = (message, type) => {
    this.systemMessage = { message, type };
  };

  clearSystemMessage = () => {
    this.systemMessage = null;
  };

  setProperty = (property, value) => {
    this.messaging[property] = value;
  };

  setSellerAction = (payload) => {
    this.sellerAction = payload;
  };

  clearSellerAction = () => {
    this.sellerAction = null;
  };

  initializeMessaging = () => {
    this.setProperty(
      'disableUserTab',
      !this.auctionStore.auctionLaneId || !this.auctionStore.connected,
    );
    this.setProperty('disableGlobalTab', !this.auctionStore.connected);
    this.setProperty(
      'disablePinnedTab',
      !this.auctionStore.auctionLaneId || !this.auctionStore.connected,
    );
  };

  handleClearMessagingObject = () => {
    this.auctionLogObject = [];
  };

  handleMessagingObject = (messageObject) => {
    this.auctionLogObject.push(messageObject);
  };

  handleSetFilteredMessageData = (data) => {
    this.messaging.filteredMessageData = data;
  };

  handleMessaging = (message, error = null, toastMessage = null) => {
    if (toastMessage) toast.error(toastMessage);
    this.auctionLog.push(message);
  };

  setLanePinnedMessage = (laneID, message) => {
    if (laneID && !message) {
      const messages = this.messaging.pinnedMessage.filter(
        (item) => item.laneID !== laneID,
      );
      this.messaging.pinnedMessage = messages;
      return;
    }

    const otherLaneMessages = this.messaging.pinnedMessage.filter(
      (item) => item.laneID !== laneID,
    );
    const withNewMessages = [
      ...otherLaneMessages,
      {
        laneID,
        message,
      },
    ];
    this.messaging.pinnedMessage = withNewMessages;
  };

  setOnlineBiddersForChat = (memberName) => {
    const allListeners = [...this.onlineChatBidders, ...this.onlineListeners, memberName];
    this.onlineChatBidders = [...new Set(allListeners)];
    // console.log('setOnlineBiddersForChat', {onlineChatBidders: this.onlineChatBidders, onlineListeners: this.onlineListeners})
  };

  setOnlineListeners = (data) => {
    this.onlineListenersMemberName = data.map((member) => member.memberName);
    this.onlineChatBidders = this.onlineListenersMemberName;

    // console.log('setOnlineListeners', {onlineChatBidders: this.onlineChatBidders, onlineListeners: this.onlineListenersMemberName})
  }

  get shouldShowPinnedMessage() {
    return !!this.messaging.pinnedMessage;
  }

  handleSetDirectMessageUser = (name, id, itemNumber, memberName) => {
    this.messaging.sendToUserId = id;
    this.messaging.sendToUserName = getTrimmedString(name);
    this.messaging.itemNumber = itemNumber;
    this.messaging.sendToMemberName = memberName;
  };

  // MessageType: MessageFromBidder, MessageToBidder, MessageToGlobal
  handleDirectMessage = (
    payload,
    auctionId,
    auctionLaneId,
    messageType,
    lotNumber,
    userType = 'Bidder',
  ) => {
    if (messageType.indexOf('Global') > -1) return;

    const { account, member, memberName, bidderNumber, message } = payload;
    const msg = {
      id: new Date().getUTCMilliseconds(),
      auctionId,
      laneId: auctionLaneId,
      content: message,
      type: messageType,
      itemNumber: lotNumber ? `${lotNumber}` : null,
      timestamp: getFormattedTime(new Date()),
      member,
      memberName,
      bidderNumber,
      account,
      to: {
        name: messageType === 'MessageToClient' ? member : 'Clerk',
        bidderNumber: messageType === 'MessageToClient' ? bidderNumber : '1234',
      },
      from: {
        name: messageType === 'MessageToClient' ? 'Clerk' : member,
        bidderNumber: messageType === 'MessageToClient' ? null : bidderNumber,
      },
      userType,
      isSeller: userType === 'Seller',
    };

    this.directMessageLog.push(msg);
  };
}

decorate(MessageStore, {
  auctionLog: observable,
  auctionLogObject: observable,
  bidActivityMappings: observable,
  clearSellerAction: action,
  clearSystemMessage: action,
  directMessageLog: observable,
  handleClearMessagingObject: action,
  handleDirectMessage: action,
  handleMessaging: action,
  handleMessagingObject: action,
  handleSetDirectMessageUser: action,
  handleSetFilteredMessageData: action,
  initializeMessaging: action,
  messaging: observable,
  sellerAction: observable,
  setLanePinnedMessage: action,
  setSellerAction: action,
  setSystemMessage: action,
  shouldShowPinnedMessage: computed,
  systemMessage: observable,
  onlineListeners: observable,
  onlineChatBidders: observable,
  setOnlineListeners: action,
  setOnlineBiddersForChat: action,
});

export default MessageStore;
